// Utility functions for automatic activity logging
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Activity Log utility functions
export const logActivity = async (activityData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const response = await fetch(`${API_BASE}/api/activity-logs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(activityData)
    });

    if (!response.ok) {
      console.warn('Failed to log activity:', response.status);
    }
  } catch (error) {
    console.warn('Error logging activity:', error);
  }
};

// Create activity log data helper
export const createActivityData = (user, actionType, documentType, documentId, documentReference = null, metadata = {}, context = {}) => {
  return {
    user_id: user.id,
    user_name: user.full_name || user.username,
    user_email: user.email,
    action_type: actionType,
    document_type: documentType,
    document_id: documentId,
    document_reference: documentReference,
    metadata: metadata,
    context: context,
    is_internal: false,
    ip_address: null, // Could be added if needed
    user_agent: navigator.userAgent
  };
};

// Activity logging hooks for common actions
export const ActivityLogger = {
  // Document actions
  documentCreated: async (user, document) => {
    const activityData = createActivityData(
      user,
      'create',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        document_type: document.type,
        client_name: document.client?.name,
        total_ttc: document.total_ttc,
        items_count: document.items?.length || 0
      }
    );
    await logActivity(activityData);
  },

  documentUpdated: async (user, document, changedFields = []) => {
    const activityData = createActivityData(
      user,
      'update',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        fields_changed: changedFields,
        document_type: document.type,
        client_name: document.client?.name
      },
      {
        action: 'update',
        fields_changed: changedFields
      }
    );
    await logActivity(activityData);
  },

  documentDeleted: async (user, document, reason = '') => {
    const activityData = createActivityData(
      user,
      'delete',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        deletion_reason: reason,
        document_type: document.type,
        client_name: document.client?.name
      }
    );
    await logActivity(activityData);
  },

  documentSent: async (user, document, recipientEmail, template = null) => {
    const activityData = createActivityData(
      user,
      'send',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        recipient_email: recipientEmail,
        template_used: template,
        document_type: document.type,
        sent_at: new Date().toISOString()
      }
    );
    await logActivity(activityData);
  },

  // Opportunity actions
  opportunityCreated: async (user, opportunity) => {
    const activityData = createActivityData(
      user,
      'create',
      'opportunite',
      opportunity.id,
      opportunity.numero_opportunite,
      {
        client_name: opportunity.client_name,
        valeur_estimee: opportunity.valeur_estimee,
        statut: opportunity.statut,
        probabilite: opportunity.probabilite
      }
    );
    await logActivity(activityData);
  },

  opportunityUpdated: async (user, opportunity, changedFields = []) => {
    const activityData = createActivityData(
      user,
      'update',
      'opportunite',
      opportunity.id,
      opportunity.numero_opportunite,
      {
        fields_changed: changedFields,
        current_status: opportunity.statut,
        valeur_estimee: opportunity.valeur_estimee
      },
      {
        action: 'update',
        fields_changed: changedFields
      }
    );
    await logActivity(activityData);
  },

  opportunityStatusChanged: async (user, opportunity, oldStatus, newStatus) => {
    const activityData = createActivityData(
      user,
      'status_change',
      'opportunite',
      opportunity.id,
      opportunity.numero_opportunite,
      {
        status_from: oldStatus,
        status_to: newStatus,
        client_name: opportunity.client_name
      },
      {
        before: { statut: oldStatus },
        after: { statut: newStatus }
      }
    );
    await logActivity(activityData);
  },

  // Operation actions
  operationCreated: async (user, operation) => {
    const activityData = createActivityData(
      user,
      'create',
      'operation',
      operation.id,
      operation.numero_operation,
      {
        client_name: operation.client_name,
        type_marchandise: operation.type_marchandise,
        mode_transport: operation.mode_transport,
        valeur_estimee: operation.valeur_estimee_xof,
        opportunite_id: operation.opportunite_id
      }
    );
    await logActivity(activityData);
  },

  operationUpdated: async (user, operation, changedFields = []) => {
    const activityData = createActivityData(
      user,
      'update',
      'operation',
      operation.id,
      operation.numero_operation,
      {
        fields_changed: changedFields,
        current_priority: operation.priorite,
        current_status: operation.statut
      },
      {
        action: 'update',
        fields_changed: changedFields
      }
    );
    await logActivity(activityData);
  },

  operationAssigned: async (user, operation, assignedTo) => {
    const activityData = createActivityData(
      user,
      'assign',
      'operation',
      operation.id,
      operation.numero_operation,
      {
        assigned_to: assignedTo,
        assignment_type: 'manual'
      }
    );
    await logActivity(activityData);
  },

  // User actions
  userCreated: async (user, newUser) => {
    const activityData = createActivityData(
      user,
      'create',
      'user',
      newUser.id,
      newUser.username,
      {
        created_user_name: newUser.full_name,
        created_user_email: newUser.email,
        created_user_role: newUser.role
      }
    );
    await logActivity(activityData);
  },

  userRoleChanged: async (user, targetUser, oldRole, newRole) => {
    const activityData = createActivityData(
      user,
      'assign',
      'user',
      targetUser.id,
      targetUser.username,
      {
        role_from: oldRole,
        role_to: newRole,
        target_user_name: targetUser.full_name
      },
      {
        before: { role: oldRole },
        after: { role: newRole }
      }
    );
    await logActivity(activityData);
  },

  // Company/Client actions
  clientCreated: async (user, client) => {
    const activityData = createActivityData(
      user,
      'create',
      'societe',
      client.id,
      client.company_name,
      {
        company_name: client.company_name,
        company_email: client.company_email,
        rccm: client.rccm,
        compte_contribuable: client.compte_contribuable
      }
    );
    await logActivity(activityData);
  },

  clientUpdated: async (user, client, changedFields = []) => {
    const activityData = createActivityData(
      user,
      'update',
      'societe',
      client.id,
      client.company_name,
      {
        fields_changed: changedFields,
        company_name: client.company_name
      },
      {
        action: 'update',
        fields_changed: changedFields
      }
    );
    await logActivity(activityData);
  },

  // Comment action
  commentAdded: async (user, documentId, documentType, comment, parentCommentId = null) => {
    const activityData = createActivityData(
      user,
      'comment',
      documentType,
      documentId,
      null,
      {
        comment_preview: comment.substring(0, 100),
        parent_comment_id: parentCommentId,
        comment_length: comment.length
      },
      {},
      true // is_internal = true for comments
    );
    await logActivity(activityData);
  },

  // Signature actions
  signatureCreated: async (user, signature) => {
    const activityData = createActivityData(
      user,
      'create',
      'signature',
      signature.id || user.id, // Use signature ID or fallback to user ID
      user.username,
      {
        signature_preview: signature.signature_text ? signature.signature_text.substring(0, 100) : '',
        is_default: signature.is_default || false,
        signature_length: signature.signature_text ? signature.signature_text.length : 0,
        user_name: user.full_name || user.username
      },
      {
        action: 'create_signature',
        user_id: user.id
      }
    );
    await logActivity(activityData);
  },

  signatureUpdated: async (user, signature, previousSignature = null) => {
    const activityData = createActivityData(
      user,
      'update',
      'signature',
      signature.id || user.id,
      user.username,
      {
        signature_preview: signature.signature_text ? signature.signature_text.substring(0, 100) : '',
        previous_preview: previousSignature ? previousSignature.signature_text.substring(0, 100) : '',
        signature_length: signature.signature_text ? signature.signature_text.length : 0,
        is_default: signature.is_default || false,
        user_name: user.full_name || user.username
      },
      {
        action: 'update_signature',
        user_id: user.id,
        has_changes: true
      }
    );
    await logActivity(activityData);
  },

  signatureDeleted: async (user, signature) => {
    const activityData = createActivityData(
      user,
      'delete',
      'signature',
      signature.id || user.id,
      user.username,
      {
        deleted_signature_preview: signature.signature_text ? signature.signature_text.substring(0, 100) : '',
        was_default: signature.is_default || false,
        user_name: user.full_name || user.username
      },
      {
        action: 'delete_signature',
        user_id: user.id
      }
    );
    await logActivity(activityData);
  },

  // Settings actions
  settingsUpdated: async (user, settingType, oldSettings, newSettings, changedFields = []) => {
    const activityData = createActivityData(
      user,
      'update',
      'settings',
      user.id,
      settingType,
      {
        setting_type: settingType,
        fields_changed: changedFields,
        user_name: user.full_name || user.username
      },
      {
        action: 'settings_update',
        setting_type: settingType,
        before: oldSettings,
        after: newSettings,
        fields_changed: changedFields
      }
    );
    await logActivity(activityData);
  },

  // Document modification tracking (detailed)
  documentFieldUpdated: async (user, document, fieldName, oldValue, newValue) => {
    const activityData = createActivityData(
      user,
      'update',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        field_name: fieldName,
        old_value: oldValue,
        new_value: newValue,
        document_type: document.type,
        client_name: document.client?.name,
        modification_type: 'field_update'
      },
      {
        action: 'field_modification',
        field: fieldName,
        before: { [fieldName]: oldValue },
        after: { [fieldName]: newValue }
      }
    );
    await logActivity(activityData);
  },

  documentStatusUpdated: async (user, document, oldStatus, newStatus) => {
    const activityData = createActivityData(
      user,
      'status_change',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        status_from: oldStatus,
        status_to: newStatus,
        document_type: document.type,
        client_name: document.client?.name
      },
      {
        action: 'status_change',
        before: { status: oldStatus },
        after: { status: newStatus }
      }
    );
    await logActivity(activityData);
  },

  documentItemsModified: async (user, document, modificationType, itemsAdded = [], itemsRemoved = [], itemsModified = []) => {
    const activityData = createActivityData(
      user,
      'update',
      getDocumentTypeForLog(document.type),
      document.id,
      document.numero_reference,
      {
        modification_type: modificationType,
        items_added_count: itemsAdded.length,
        items_removed_count: itemsRemoved.length,
        items_modified_count: itemsModified.length,
        total_items: document.items?.length || 0,
        document_type: document.type,
        client_name: document.client?.name
      },
      {
        action: 'items_modification',
        items_added: itemsAdded,
        items_removed: itemsRemoved,
        items_modified: itemsModified
      }
    );
    await logActivity(activityData);
  }
};

// Helper function to map document types
const getDocumentTypeForLog = (docType) => {
  const typeMap = {
    'devis': 'devis',
    'bon_commande': 'bon_commande',
    'facture': 'facture'
  };
  return typeMap[docType] || docType;
};

// Export default for easy import
export default ActivityLogger;